import { __assign, __awaiter, __extends, __generator, __spreadArrays } from "tslib";
import { isArray } from '@tyler-components-web/core';
import { ICON_CLASS_NAME } from '../constants';
import { randomChars } from '../core';
import { CascadingListDropdownAwareFoundation, ListDropdownAsyncStyle, ListDropdownType } from '../list-dropdown';
import { isListDropdownOptionType, ListDropdownOptionType } from '../list-dropdown/list-dropdown-utils';
import { PopupPlacement } from '../popup';
import { MENU_CONSTANTS } from './menu-constants';
var MenuFoundation = /** @class */ (function (_super) {
    __extends(MenuFoundation, _super);
    function MenuFoundation(_adapter) {
        var _this = _super.call(this, {
            popupTimeout: MENU_CONSTANTS.numbers.POPUP_MOUSE_LEAVE_TIMEOUT,
            targetTimeout: MENU_CONSTANTS.numbers.CHILD_MOUSE_LEAVE_TIMEOUT
        }) || this;
        _this._adapter = _adapter;
        _this._placement = PopupPlacement.BottomLeft;
        _this._dense = false;
        _this._iconClass = ICON_CLASS_NAME;
        _this._persistSelection = false;
        _this._mode = 'click';
        _this._identifier = randomChars();
        _this._clickListener = function (evt) { return _this._onTargetClick(evt); };
        _this._blurListener = function (evt) { return _this._onTargetBlur(evt); };
        _this._selectListener = function (value) { return _this._handleSelectEvent(value); };
        _this._keydownListener = function (evt) { return _this.onKeydown(evt); };
        _this._activeChangeListener = function (id) { return _this._onActiveOptionChanged(id); };
        return _this;
    }
    MenuFoundation.prototype.initialize = function () {
        this._adapter.initializeTarget();
        this._adapter.initializeAccessibility(this._identifier);
        this._initializeInteractionListeners();
    };
    MenuFoundation.prototype.disconnect = function () {
        if (this._open) {
            this._closeDropdown();
        }
        this._destroyInteractionListeners();
    };
    MenuFoundation.prototype._applyMode = function () {
        if (!this._adapter.hasTargetElement()) {
            return;
        }
        if (this._mode !== 'cascade') {
            this._adapter.addTargetListener('click', this._clickListener);
        }
        else {
            this._attachCascadingListeners();
        }
    };
    MenuFoundation.prototype._initializeInteractionListeners = function () {
        if (!this._adapter.hasTargetElement()) {
            return;
        }
        this._applyMode();
        this._adapter.addTargetListener('keydown', this._keydownListener, true);
        this._adapter.addTargetListener('blur', this._blurListener);
    };
    MenuFoundation.prototype._destroyInteractionListeners = function () {
        if (!this._adapter.hasTargetElement()) {
            return;
        }
        this._adapter.removeTargetListener('keydown', this._clickListener);
        this._adapter.removeTargetListener('blur', this._blurListener);
        this._adapter.removeTargetListener('click', this._clickListener);
        this._detachCascadingListeners();
    };
    MenuFoundation.prototype._attachCascadingListeners = function () {
        this._adapter.addTargetListener('mouseenter', this._targetMouseEnterListener);
        this._adapter.addTargetListener('mouseleave', this._targetMouseLeaveListener);
        // We listen for document mousemove events only to track mouse coords for use when determining
        // if popups should open/close after specific delays
        this._adapter.addDocumentListener('mousemove', this._documentMouseMoveListener);
    };
    MenuFoundation.prototype._detachCascadingListeners = function () {
        this._adapter.removeTargetListener('mouseenter', this._targetMouseEnterListener);
        this._adapter.removeTargetListener('mouseleave', this._targetMouseLeaveListener);
        this._adapter.removeDocumentListener('mousemove', this._documentMouseMoveListener);
    };
    Object.defineProperty(MenuFoundation.prototype, "_nonDividerOptions", {
        get: function () {
            return this._flatOptions.filter(function (o) { return !o.divider; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "_flatOptions", {
        get: function () {
            if (isListDropdownOptionType(this._options, ListDropdownOptionType.Group)) {
                return this._options.reduce(function (previousValue, currentValue) { return previousValue.concat(currentValue.options); }, []);
            }
            return this._options;
        },
        enumerable: true,
        configurable: true
    });
    MenuFoundation.prototype._onTargetClick = function (evt) {
        if (this._open) {
            this._closeDropdown();
            return;
        }
        this.open = !this._open;
        if (this._open) {
            this._adapter.focusTarget();
        }
    };
    MenuFoundation.prototype._onTargetBlur = function (evt) {
        if (this._open) {
            this._closeDropdown();
        }
    };
    MenuFoundation.prototype._onActiveOptionChanged = function (id) {
        // We need to dispatch an event when the active descendant changes to propagate that to the proper target element
        var data = { id: id };
        var canSetActiveDescendant = this._adapter.emitHostEvent(MENU_CONSTANTS.events.ACTIVE_CHANGE, data, true, true);
        if (this._mode !== 'cascade' && canSetActiveDescendant) {
            this._adapter.updateActiveDescendant(id);
        }
    };
    MenuFoundation.prototype.onKeydown = function (evt) {
        // If we have any child menus open, we need to proxy the keyboard events to those and exit
        if (this._childOpen) {
            // If escape is pressed while other menus are open then we need to close everything
            if (evt.code === 'Escape') {
                evt.preventDefault();
                this._adapter.closeOtherChildMenus();
                this._closeDropdown();
                return;
            }
            this._adapter.proxyKeyboardEventToChild(evt, this._identifier);
            return;
        }
        switch (evt.code) {
            case 'Escape':
                if (this._open) {
                    evt.preventDefault();
                    this._closeDropdown();
                }
                break;
            case 'Space':
                evt.preventDefault();
                if (this._open) {
                    this._closeDropdown();
                }
                else {
                    this._openDropdown();
                }
                break;
            case 'Tab':
                if (this._open) {
                    this._closeDropdown();
                }
                break;
            case 'Home':
            case 'End':
                evt.preventDefault();
                this._adapter.propagateKey(evt.code);
                break;
            case 'Enter':
                if (this._open) {
                    evt.stopImmediatePropagation();
                }
                evt.preventDefault();
                if (!this._open) {
                    this._openDropdown();
                    return;
                }
                this._adapter.propagateKey(evt.code);
                // Check if we need to toggle the child menu open state
                var activeIndex = this._adapter.getActiveOptionIndex();
                var activeOption = this._nonDividerOptions[activeIndex];
                if (activeOption && activeOption.options && activeOption.options.length) {
                    this._adapter.toggleChildMenu(activeIndex);
                }
                break;
            case 'ArrowUp':
            case 'ArrowDown':
                if (this._open) {
                    evt.stopImmediatePropagation();
                }
                evt.preventDefault();
                if (!this._open && evt.code === 'ArrowDown') {
                    this._openDropdown();
                    return;
                }
                this._adapter.propagateKey(evt.code);
                break;
            case 'ArrowLeft':
                if (this._open) {
                    evt.stopImmediatePropagation();
                }
                if (this._mode === 'cascade' && this._open) {
                    this._closeDropdown();
                }
                break;
            case 'ArrowRight':
                if (this._open) {
                    evt.stopImmediatePropagation();
                }
                if (!this._open) {
                    return;
                }
                var hoveredIndex = this._adapter.getActiveOptionIndex();
                var hoveredOption = this._nonDividerOptions[hoveredIndex];
                if (hoveredOption && hoveredOption.options) {
                    this._adapter.toggleChildMenu(hoveredIndex, evt.code === 'ArrowRight');
                    return;
                }
                break;
        }
    };
    MenuFoundation.prototype._openMenu = function () {
        return __awaiter(this, void 0, void 0, function () {
            var options, flatOptions, selectedValues, config;
            var _this = this;
            return __generator(this, function (_a) {
                this._open = true;
                options = [];
                if (typeof this._optionsFactory === 'function') {
                    this._loadOptionsAsync();
                    this._options = [];
                }
                else {
                    if (!this._options || !this._options.length) {
                        return [2 /*return*/];
                    }
                    options = this._options;
                }
                if (!this._persistSelection) {
                    options.forEach(function (o) { return o.selected = false; });
                }
                flatOptions = this._flatOptions;
                // Map the old "icon" property to the new "leadingIcon" property (if exists)
                flatOptions.filter(function (o) { return o.icon; }).forEach(function (o) { return o.leadingIcon = o.icon; });
                selectedValues = this._persistSelection ? this._getSelectedValues() : [];
                config = {
                    id: this._identifier,
                    type: ListDropdownType.Menu,
                    options: this._options,
                    selectedValues: selectedValues,
                    iconClass: this._iconClass,
                    dense: this._dense,
                    multiple: false,
                    popupStatic: true,
                    allowBusy: true,
                    asyncStyle: ListDropdownAsyncStyle.Spinner,
                    optionBuilder: this._optionBuilder,
                    optionLimit: this._optionLimit,
                    headerBuilder: this._popupHeaderBuilder,
                    footerBuilder: this._popupFooterBuilder,
                    observeScroll: this._observeScroll,
                    observeScrollThreshold: this._observeScrollThreshold,
                    dialogWidthThreshold: 0,
                    popupPlacement: this._placement,
                    popupClasses: __spreadArrays([
                        MENU_CONSTANTS.classes.POPUP,
                        MENU_CONSTANTS.classes.MENU
                    ], this._popupClasses),
                    syncWidth: this._syncPopupWidth,
                    highlightFirst: this._highlightFirst,
                    activeChangeCallback: this._activeChangeListener,
                    selectCallback: this._selectListener,
                    popupPositionAdjustment: this._popupPositionAdjustment,
                    cascadingElementFactory: function (params) { return _this._createCascadingElement(params); }
                };
                this._adapter.setHostAttribute(MENU_CONSTANTS.attributes.OPEN, '');
                this._adapter.attachMenu(config);
                // If this menu is in a hover mode, we need to listen for mouse enter/leave events to know when the user is hovering over the popup
                if (this._mode === 'cascade') {
                    this._adapter.addDropdownListener('mouseenter', this._childPopupMouseEnterListener);
                    this._adapter.addDropdownListener('mouseleave', this._childPopupMouseLeaveListener);
                }
                this._adapter.addDropdownListener(MENU_CONSTANTS.events.ACTIVE_CHANGE, function (evt) {
                    _this._onActiveOptionChanged(evt.detail.id);
                });
                this._adapter.emitHostEvent(MENU_CONSTANTS.events.OPEN);
                return [2 /*return*/];
            });
        });
    };
    MenuFoundation.prototype._closeMenu = function () {
        this._open = false;
        this._childOpen = false;
        this._adapter.detachMenu();
        if (this._activeMouseLeaveTimeout !== undefined) {
            window.clearTimeout(this._activeMouseLeaveTimeout);
        }
        this._adapter.removeHostAttribute(MENU_CONSTANTS.attributes.OPEN);
        this._adapter.emitHostEvent(MENU_CONSTANTS.events.CLOSE);
    };
    MenuFoundation.prototype._loadOptionsAsync = function () {
        var _this = this;
        return Promise.resolve(this._optionsFactory())
            .then(function (results) {
            if (!_this._persistSelection) {
                results.forEach(function (o) { return o.selected = false; });
            }
            if (_this._open) {
                if (results && isArray(results) && results.length) {
                    _this._options = results;
                    _this._adapter.setOptions(results);
                    var selectedValues = _this._getSelectedValues();
                    if (selectedValues.length) {
                        _this._adapter.setSelectedValues(selectedValues);
                    }
                    if (_this._options.length && _this._highlightFirst) {
                        _this._adapter.activateFirstOption();
                    }
                }
                else if (_this._open) {
                    _this._closeDropdown();
                }
            }
        })
            .catch(function (e) {
            console.error('An unexpected error ocurred while opening the menu:', e);
            if (_this._open) {
                _this._closeDropdown();
            }
        });
    };
    MenuFoundation.prototype._handleSelectEvent = function (value) {
        var index = this._nonDividerOptions.findIndex(function (o) { return o.value === value; });
        if (index < 0) {
            return;
        }
        this._selectOptionByIndex(index);
    };
    MenuFoundation.prototype._selectOptionByIndex = function (index) {
        var _a = this._nonDividerOptions[index], options = _a.options, value = _a.value;
        // We ignore selections if the option has child options
        if (options) {
            this._adapter.setActiveOption(index);
            return;
        }
        this._closeDropdown();
        var data = { index: index, value: value };
        var isCancelled = !this._adapter.emitHostEvent(MENU_CONSTANTS.events.SELECT, data, true, true);
        if (!isCancelled) {
            this._selectedValue = this._persistSelection ? value : undefined;
        }
    };
    MenuFoundation.prototype._getSelectedValues = function () {
        var values = this._flatOptions.filter(function (o) { return o.selected; }).map(function (o) { return o.value; });
        if (this._selectedValue !== undefined) {
            values.push(this._selectedValue);
        }
        return values;
    };
    /** Called when a child menu option is selected. */
    MenuFoundation.prototype._onCascadingOptionSelected = function (data) {
        if (this._persistSelection) {
            this._selectedValue = data.value;
        }
        this._adapter.emitHostEvent(MENU_CONSTANTS.events.SELECT, data);
        this._closeDropdown();
    };
    /** Called when a child menu is opened off of one of our menu options. */
    MenuFoundation.prototype._onCascadingChildOpen = function (index) {
        _super.prototype._clearMouseLeaveTimeout.call(this);
        this._adapter.closeOtherChildMenus(index);
        this._childOpen = true;
        this._adapter.setActiveOption(index);
    };
    /** Called when a child menu is closed off of one of our menu options. */
    MenuFoundation.prototype._onCascadingChildClose = function () {
        this._childOpen = false;
    };
    MenuFoundation.prototype._closeDropdown = function () {
        this._closeMenu();
    };
    MenuFoundation.prototype._openDropdown = function () {
        this._openMenu();
    };
    MenuFoundation.prototype._isOwnElement = function (element) {
        return this._adapter.isOwnElement(element);
    };
    MenuFoundation.prototype._createCascadingElement = function (_a) {
        var index = _a.index, options = _a.options, parentValue = _a.parentValue;
        var menu = this._adapter.createChildMenu(index, parentValue, this._onCascadingChildOpen.bind(this), this._onCascadingChildClose.bind(this), this._onCascadingOptionSelected.bind(this));
        menu.mode = 'cascade';
        menu.popupPositionAdjustment = { top: -8, bottom: 0, left: 0, right: 0 };
        menu.dense = this._dense;
        menu.placement = PopupPlacement.RightTop;
        menu.persistSelection = this._persistSelection;
        if (this._persistSelection) {
            menu.selectedValue = this._selectedValue;
        }
        menu.options = options;
        menu.optionBuilder = this._optionBuilder;
        menu.iconClass = this._iconClass;
        return menu;
    };
    Object.defineProperty(MenuFoundation.prototype, "open", {
        get: function () {
            return this._open;
        },
        set: function (value) {
            value = Boolean(value);
            if (this._open !== value) {
                this._open = value;
                if (value) {
                    this._openDropdown();
                }
                else {
                    this._closeDropdown();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "options", {
        get: function () {
            if (!this._options) {
                return [];
            }
            // Intentional shallow copy of member properties. These member objects have properties that are references to functions.
            //   The typical JSON.parse(JSON.stringify(object)) will not work here. If this becomes an issue we'll add a deepClone
            //   function to the core library.
            return this._flatOptions.map(function (o) { return (__assign({}, o)); });
        },
        set: function (options) {
            if (typeof options === 'function') {
                this.optionsFactory = options;
            }
            else {
                this.optionsFactory = undefined;
                // Intentional shallow copy of member properties. These member objects have properties that are references to functions.
                //   The typical JSON.parse(JSON.stringify(object)) will not work here. If this becomes an issue we'll add a deepClone
                //   function to the core library.
                this._options = options.map(function (o) { return (__assign({}, o)); });
                if (this._open) {
                    this._adapter.setOptions(this._options);
                    if (this._persistSelection) {
                        var selectedValues = this._getSelectedValues();
                        if (selectedValues.length) {
                            this._adapter.setSelectedValues(selectedValues);
                        }
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "optionsFactory", {
        set: function (factory) {
            this._optionsFactory = factory;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "selectedIndex", {
        get: function () {
            var _this = this;
            return this._selectedValue !== undefined ? this._nonDividerOptions.findIndex(function (o) { return o.value === _this._selectedValue; }) : -1;
        },
        set: function (value) {
            var option = this._nonDividerOptions[value];
            if (option && option.value !== this._selectedValue) {
                this.selectedValue = option.value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "selectedValue", {
        get: function () {
            return this._selectedValue;
        },
        set: function (value) {
            if (this._selectedValue !== value) {
                this._selectedValue = value;
                this._adapter.setSelectedValues(this._selectedValue);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "placement", {
        get: function () {
            return this._placement;
        },
        set: function (value) {
            if (this._placement !== value) {
                this._placement = value || PopupPlacement.BottomLeft;
                this._adapter.setHostAttribute(MENU_CONSTANTS.attributes.PLACEMENT, this._placement);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "dense", {
        get: function () {
            return this._dense;
        },
        set: function (value) {
            this._dense = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "iconClass", {
        get: function () {
            return this._iconClass;
        },
        set: function (value) {
            this._iconClass = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "persistSelection", {
        get: function () {
            return this._persistSelection;
        },
        set: function (value) {
            if (this._persistSelection === value) {
                return;
            }
            this._persistSelection = value;
            this._adapter.toggleHostAttribute(MENU_CONSTANTS.attributes.PERSIST_SELECTION, this._persistSelection);
            if (!this._persistSelection) {
                this._flatOptions.forEach(function (o) { return o.selected = false; });
                this._selectedValue = undefined;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "mode", {
        get: function () {
            return this._mode;
        },
        set: function (value) {
            if (this._mode !== value) {
                this._mode = value;
                this._destroyInteractionListeners();
                this._applyMode();
                this._adapter.setHostAttribute(MENU_CONSTANTS.attributes.MODE, this._mode);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "popupPositionAdjustment", {
        get: function () {
            return this._popupPositionAdjustment;
        },
        set: function (value) {
            this._popupPositionAdjustment = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "optionBuilder", {
        get: function () {
            return this._optionBuilder;
        },
        set: function (cb) {
            this._optionBuilder = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MenuFoundation.prototype, "popupElement", {
        get: function () {
            return this._adapter.getDropdownElement();
        },
        enumerable: true,
        configurable: true
    });
    return MenuFoundation;
}(CascadingListDropdownAwareFoundation));
export { MenuFoundation };
